import Close from '@mui/icons-material/Close';
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  CompanyShare_Create,
  CompanyShare_Update,
  CompanyShareableType,
  deleteV1CompaniesCompanyIdCompanySharesId,
  deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId,
  getV1CompaniesCompanyIdCompanyShares,
  getV1CompaniesCompanyIdDriversId,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanyShares,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDriversId,
  ModelError_Item,
  ModelError_Response,
  patchV1CompaniesCompanyIdAccountsId,
  patchV1CompaniesCompanyIdCompanySharesId,
  patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdAccountsId,
  patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId,
  postV1CompaniesCompanyIdCompanyShares,
  postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanyShares,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import {
  UserForm,
  UserFormHandler,
  UserValidationDTOWithSitesDiff,
} from '~components/UserForm/v2/UserForm';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { Driver, DriverTypeahead } from '~hooks/useDrivers/models';
import { User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

enum SaveDriverStep {
  SAVING_DRIVER = 'saving-driver',
  SAVING_SHARED_DRIVER_CONFIGURATION = 'saving-shared-driver-configuration',
}

interface EditDriverDrawerProps {
  driver?: DriverTypeahead;
  onClose: (updatedDriver?: Driver) => void;
}

export default function EditDriverDrawer({
  driver: driverProp,
  onClose,
}: EditDriverDrawerProps) {
  const isOpen = Boolean(driverProp?.id);

  const { userStore, ordersDispatchStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const showDriverTrailerSettingEnabled =
    userStore.companySettings.isEnabled('show_driver_trailer');

  const driverFormRef = useRef<UserFormHandler>(null);
  const { updateUser } = useUsers();

  const [driverFormData, setDriverFormData] = useState<{
    driver: Driver;
    companyShares: CompanyShare[];
  }>();
  const [saveDriverStep, setSaveDriverStep] = useState<SaveDriverStep>();
  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);

  // If driver is managed, use the driver's company ID for managed company endpoints
  const driverCompanyId = driverProp?.managed ? driverProp.company.id : undefined;

  // if the driver is shared from a company other than the driver's company, then a vendor is involved
  const isDriverManagedByVendor =
    driverProp && driverProp?.companyShare?.senderCompany?.id !== driverProp?.company.id;

  const vendorCompanyId = isDriverManagedByVendor
    ? driverProp?.companyShare?.senderCompany?.id
    : undefined;

  const managedCompanyId = vendorCompanyId ?? driverCompanyId ?? undefined;

  const updateDriver = useCallback(
    async (user: User, managedCompanyIdParam?: string) => {
      try {
        const updatedDriver = await updateUser(
          user,
          undefined,
          managedCompanyIdParam ? companyId : user.company?.id,
          managedCompanyIdParam,
        );

        return { driver: updatedDriver as unknown as Driver };
      } catch (error) {
        const isUnprocessableEntityError = isAxiosError(error) && error.status === 422;

        if (isUnprocessableEntityError) {
          const typedError = error as AxiosError<ModelError_Response>;
          const errors = typedError.response?.data.error.errors ?? [];

          return { errors };
        }

        throw error;
      }
    },
    [companyId, updateUser],
  );

  const updateCompanyShares = useCallback(
    async (
      driverId: string,
      oldCompanyShares: CompanyShare[],
      newCompanyShares: NonNullable<UserValidationDTOWithSitesDiff['companyShares']>,
      managedCompanyIdParam?: string,
    ) => {
      const { toCreate, toUpdate } = newCompanyShares.reduce(
        (obj, newCompanyShare) => {
          const isInBothLists = oldCompanyShares.some((oldCompanyShare) => {
            return oldCompanyShare.id === newCompanyShare.id;
          });

          if (isInBothLists) {
            obj.toUpdate.push({
              rate_override: Boolean(newCompanyShare.rateOverride),
              companyShareId: newCompanyShare.id as string,
            });
          } else {
            obj.toCreate.push({
              receiver_company_id: newCompanyShare.receiverCompany.id as string,
              company_shareable_id: driverId,
              company_shareable_type: CompanyShareableType.USER,
              rate_override: Boolean(newCompanyShare.rateOverride),
            });
          }

          return obj;
        },
        { toCreate: [], toUpdate: [] } as {
          toCreate: Array<CompanyShare_Create>;
          toUpdate: Array<CompanyShare_Update & { companyShareId: string }>;
        },
      );

      const toDelete = oldCompanyShares.reduce(
        (acc, oldCompanyShare) => {
          const isDeleted = !newCompanyShares.some((newCompanyShare) => {
            return oldCompanyShare.id === newCompanyShare.id;
          });

          if (isDeleted) {
            acc.push({ companyShareId: oldCompanyShare.id });
          }

          return acc;
        },
        [] as Array<{ companyShareId: string }>,
      );

      const toCreatePromises = toCreate.map((payload) =>
        managedCompanyIdParam
          ? postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanyShares({
              path: {
                'company-id': companyId,
                'managed-company-id': managedCompanyIdParam,
              },
              body: payload,
            })
          : postV1CompaniesCompanyIdCompanyShares({
              path: { 'company-id': companyId },
              body: payload,
            }),
      );

      const toUpdatePromises = toUpdate.map((payload) => {
        const { companyShareId, ...body } = payload;
        return managedCompanyIdParam
          ? patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId({
              path: {
                'company-id': companyId,
                'managed-company-id': managedCompanyIdParam,
                id: companyShareId,
              },
              body,
            })
          : patchV1CompaniesCompanyIdCompanySharesId({
              path: { 'company-id': companyId, id: companyShareId },
              body,
            });
      });

      const toDeletePromises = toDelete.map((payload) =>
        managedCompanyIdParam
          ? deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId({
              path: {
                'company-id': companyId,
                'managed-company-id': managedCompanyIdParam,
                id: payload.companyShareId,
              },
            })
          : deleteV1CompaniesCompanyIdCompanySharesId({
              path: { 'company-id': companyId, id: payload.companyShareId },
            }),
      );

      await Promise.all([...toCreatePromises, ...toUpdatePromises, ...toDeletePromises]);
    },
    [companyId],
  );

  const updateDefaultEquipment = useCallback(
    async (
      companyShareId: string,
      defaultEquipmentId: string | null,
      managedCompanyIdParam?: string,
    ) => {
      if (managedCompanyIdParam) {
        await patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId({
          path: {
            'company-id': companyId,
            'managed-company-id': managedCompanyIdParam,
            id: companyShareId,
          },
          body: { default_equipment_id: defaultEquipmentId },
        });
      } else {
        await patchV1CompaniesCompanyIdCompanySharesId({
          path: { 'company-id': companyId, id: companyShareId },
          body: { default_equipment_id: defaultEquipmentId },
        });
      }
    },
    [companyId],
  );

  const updateDefaultAdditionalEquipment = useCallback(
    async (
      companyShareId: string,
      additionalEquipmentId: string | null,
      managedCompanyIdParam?: string,
    ) => {
      const body = {
        default_additional_equipment_ids: additionalEquipmentId
          ? [additionalEquipmentId]
          : [],
      };

      if (managedCompanyIdParam) {
        await patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId({
          path: {
            'company-id': companyId,
            'managed-company-id': managedCompanyIdParam,
            id: companyShareId,
          },
          body,
        });
      } else {
        await patchV1CompaniesCompanyIdCompanySharesId({
          path: { 'company-id': companyId, id: companyShareId },
          body,
        });
      }
    },
    [companyId],
  );

  const updateAccount = useCallback(
    async (
      receiverAccountId: string,
      accountName?: string,
      accountExternalId?: string,
      managedCompanyIdParam?: string,
    ) => {
      if (managedCompanyIdParam) {
        await patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdAccountsId({
          path: {
            'company-id': companyId,
            'managed-company-id': managedCompanyIdParam,
            id: receiverAccountId,
          },
          body: { name: accountName, external_id: accountExternalId },
        });
      } else {
        await patchV1CompaniesCompanyIdAccountsId({
          path: { 'company-id': companyId, id: receiverAccountId },
          body: { name: accountName, external_id: accountExternalId },
        });
      }
    },
    [companyId],
  );

  const handleSubmit = useCallback(async () => {
    const driverId = driverFormData?.driver.id;

    if (!driverId) {
      return;
    }

    const data = await new Promise<UserValidationDTOWithSitesDiff | undefined>(
      (resolve) => {
        driverFormRef.current?.submit((data) => resolve(data));
      },
    );

    if (!data) {
      return;
    }

    setAsyncErrors([]);
    setSaveDriverStep(SaveDriverStep.SAVING_DRIVER);

    let updatedDriver: Driver | undefined = undefined;

    const isSharedDriver = Boolean(driverFormData?.driver.companyShare?.id);
    const isManagedDriver = driverFormData?.driver.managed;
    const canEditDriverDetails = !isSharedDriver || isManagedDriver;

    if (canEditDriverDetails) {
      try {
        const { additionalEquipment: _discardedAE, ...userDataWithoutAE } = data;
        const userData =
          isSharedDriver || !showDriverTrailerSettingEnabled ? userDataWithoutAE : data;

        const { driver, errors } = await updateDriver(
          { id: driverId, ...userData } as unknown as User,
          driverCompanyId ?? undefined,
        );

        setAsyncErrors(errors ?? []);

        if (driver) {
          updatedDriver = driver;
        } else {
          setSaveDriverStep(undefined);
        }
      } catch (error) {
        setSaveDriverStep(undefined);
        connection.handleRequestError(
          error,
          t('error_messages.drivers.failed_to_update'),
        );
      }
    } else {
      // Shared but not managed - skip driver update, proceed with share config only
      updatedDriver = driverFormData?.driver;
    }

    if (updatedDriver) {
      try {
        setSaveDriverStep(SaveDriverStep.SAVING_SHARED_DRIVER_CONFIGURATION);

        const prunedNewCompanySharesList = data.companyShares?.filter((companyShare) => {
          return Boolean(companyShare.receiverCompany.id);
        });

        await updateCompanyShares(
          driverId,
          driverFormData?.companyShares ?? [],
          prunedNewCompanySharesList ?? [],
          managedCompanyId,
        );

        if (driverFormData.driver.companyShare?.id) {
          await updateDefaultEquipment(
            driverFormData.driver.companyShare.id,
            data.defaultDriverEquipment?.id ?? null,
            managedCompanyId,
          );

          if (showDriverTrailerSettingEnabled) {
            await updateDefaultAdditionalEquipment(
              driverFormData.driver.companyShare.id,
              data.defaultDriverAdditionalEquipment?.id ?? null,
              managedCompanyId,
            );
          }
        }

        // if the account is owned by a vendor, then the managed company id is the vendor company id
        // otherwise, we can just use the regular non-managed-company endpoint for patching account
        if (driverFormData.driver.companyShare?.receiverAccount?.id) {
          await updateAccount(
            driverFormData.driver.companyShare.receiverAccount.id,
            data.accountName,
            data.accountExternalId,
            vendorCompanyId,
          );
        }

        setSaveDriverStep(undefined);
        ordersDispatchStore.setPendingFiltersUpdateAfterAssigneeUpdate({
          label: updatedDriver.fullName,
          value: updatedDriver.id,
        });

        onClose(updatedDriver);
      } catch (error) {
        setSaveDriverStep(undefined);
        connection.handleRequestError(
          error,
          t('error_messages.drivers.failed_to_update'),
        );
      }
    }
  }, [
    driverFormData?.companyShares,
    driverFormData?.driver,
    managedCompanyId,
    onClose,
    ordersDispatchStore,
    showDriverTrailerSettingEnabled,
    updateAccount,
    updateCompanyShares,
    updateDefaultAdditionalEquipment,
    updateDefaultEquipment,
    updateDriver,
  ]);

  useEffect(() => {
    if (!isOpen || !driverProp?.id) return;

    // The drawer stays mounted after it closes, so a request that settles late would
    // repopulate the form with the previous driver until the next fetch lands.
    const controller = new AbortController();
    const { signal } = controller;

    const fetchDriverData = async () => {
      // Scope on `managedCompanyId`, the id every other call site here threads.
      // `vendorCompanyId` alone sends a managed driver that has no onward vendor
      // share to the viewer-scoped endpoint, which cannot resolve it, and reads its
      // shares from a different scope than `updateCompanyShares` writes them to.
      const driverResponse = managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDriversId({
            path: {
              'company-id': companyId,
              'managed-company-id': managedCompanyId,
              id: driverProp.id,
            },
            signal,
          })
        : await getV1CompaniesCompanyIdDriversId({
            path: { 'company-id': companyId, id: driverProp.id },
            signal,
          });

      const driver = Driver.parse(driverResponse.data.data);

      const query = {
        'page[limit]': MAX_API_PAGE_SIZE,
        'filter[company_shareable_type]': CompanyShareableType.USER,
        'filter[company_shareable_id]': driverProp.id,
        'filter[sender_company_id]': driver.company.id,
      };

      const companySharesResponse = managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanyShares({
            path: {
              'company-id': companyId,
              'managed-company-id': managedCompanyId,
            },
            query,
            signal,
          })
        : await getV1CompaniesCompanyIdCompanyShares({
            path: { 'company-id': companyId },
            query,
            signal,
          });

      const companyShares = companySharesResponse.data.data.map((companyShare) =>
        CompanyShare.parse(companyShare),
      );

      if (signal.aborted) return;

      setDriverFormData({ driver, companyShares });
    };

    fetchDriverData().catch((error) => {
      if (signal.aborted) return;

      try {
        connection.handleRequestError(
          error,
          t('error_messages.drivers.failed_to_fetch_driver'),
        );
      } catch {
        // Error already handled (toast + Sentry)
      }
    });

    return () => {
      controller.abort();
      setAsyncErrors([]);
      setDriverFormData(undefined);
      setSaveDriverStep(undefined);
    };
  }, [companyId, driverProp?.id, isOpen, managedCompanyId]);

  return (
    <Drawer
      anchor="right"
      onClose={() => onClose()}
      open={isOpen}
      variant="temporary"
      sx={{
        [`& .${drawerClasses.paper}`]: {
          height: 'calc(100dvh - var(--tread-agent-dock-height, 0px))',
          maxWidth: '600px',
          overflow: 'hidden',
          width: '80%',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {driverFormData ? (
          <>
            <DrawerHeader onClose={() => onClose()} sx={{ flex: 0 }} />

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <UserForm
                ref={driverFormRef}
                allowSharing
                availableAuthMethods={[]}
                companyShares={driverFormData.companyShares}
                defaultDriver={driverFormData.driver}
                defaultUser={null}
                editable
                errors={asyncErrors}
                managedCompanyId={managedCompanyId}
                onFormStateChange={() => {}}
              />
            </Box>

            <DrawerFooter
              hasAsyncErrors={Boolean(asyncErrors.length)}
              onCancel={() => onClose()}
              onSave={handleSubmit}
              saveDriverStep={saveDriverStep}
              sx={{ flex: 0 }}
            />
          </>
        ) : (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            <LoadingSpinner isVisible />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

interface DrawerHeaderProps {
  onClose: () => void;
  sx?: SxProps<Theme>;
}

function DrawerHeader({ onClose, sx }: DrawerHeaderProps) {
  return (
    <Box
      sx={[
        (theme) => ({
          alignItems: 'center',
          borderBottom: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: '16px',
          fontWeight: 600,
        })}
      >
        {t('user.update_driver')}
      </Typography>
      <IconButton
        color="inherit"
        size="small"
        onClick={onClose}
        data-ui="page-dispatch__edit-driver-drawer__close-button"
      >
        <Close sx={{ width: 16, height: 16 }} />
      </IconButton>
    </Box>
  );
}

interface DrawerFooterProps {
  hasAsyncErrors?: boolean;
  onCancel: () => void;
  onSave: () => void;
  saveDriverStep?: SaveDriverStep;
  sx?: SxProps<Theme>;
}

function DrawerFooter({
  hasAsyncErrors,
  onCancel,
  onSave,
  saveDriverStep,
  sx,
}: DrawerFooterProps) {
  const isSubmitting = Boolean(saveDriverStep);

  const saveButtonTextBySaveDriverStep: Record<SaveDriverStep, string> = {
    [SaveDriverStep.SAVING_DRIVER]: t('administration.drivers.saving_driver'),
    [SaveDriverStep.SAVING_SHARED_DRIVER_CONFIGURATION]: t(
      'administration.drivers.saving_shared_driver_configuration',
    ),
  };

  return (
    <Box
      sx={[
        (theme) => ({
          alignItems: 'center',
          borderTop: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          gap: 1,
          height: '100%',
          justifyContent: 'flex-end',
          px: 2,
          py: 1,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {hasAsyncErrors && (
        <ShakyErrorIndicator
          message={t('administration.users.user_form.invalid_data_error_messages')}
        />
      )}

      <SmallButton
        color="secondary"
        disabled={isSubmitting}
        onClick={onCancel}
        variant="outlined"
      >
        {t('actions.cancel')}
      </SmallButton>

      <SmallLoadingButton
        loading={isSubmitting}
        loadingPosition="start"
        onClick={onSave}
        startIcon={isSubmitting ? <Save sx={{ color: 'transparent' }} /> : <></>}
        sx={isSubmitting ? {} : { '& .MuiButton-startIcon': { mx: 0 } }}
      >
        {saveDriverStep
          ? saveButtonTextBySaveDriverStep[saveDriverStep]
          : t('actions.update')}
      </SmallLoadingButton>
    </Box>
  );
}
